/**
 * VoiceControls Component
 *
 * Provides UI controls for:
 * - TTS playback (play/pause/stop/mute)
 * - Real-time STT (tap-to-record with live transcription)
 * - Volume visualization
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Box, IconButton, LinearProgress, Typography, Paper } from "@mui/material";
import {
  PlayArrow,
  Pause,
  Stop,
  VolumeUp,
  VolumeOff,
  Mic,
  RadioButtonChecked,
  PowerSettingsNew,
} from "@mui/icons-material";
import { AudioPlayer, PlaybackState } from "../../lib/voice/audio-player";
import {
  RealtimeSpeechRecorder,
  RecorderState,
} from "../../lib/voice/speech-recorder";
import { logService } from "../../services/logService";

interface VoiceControlsProps {
  proxyUrl: string;
  narrative: string | null;
  onTranscript: (text: string) => void;
  sfxPlayer?: (id: string) => void;
  autoPlay?: boolean;
}

export function VoiceControls({
  proxyUrl,
  narrative,
  onTranscript,
  sfxPlayer,
}: VoiceControlsProps) {
  const playerRef = useRef<AudioPlayer | null>(null);
  const recorderRef = useRef<RealtimeSpeechRecorder | null>(null);
  const narrativeLoadedRef = useRef<string | null>(null);

  // Playback state
  const [playback, setPlayback] = useState<PlaybackState>("idle");
  const [muted, setMuted] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [progress, setProgress] = useState({ cur: 0, total: 0 });

  // Recording state
  const [recording, setRecording] = useState<RecorderState>("idle");
  const [transcript, setTranscript] = useState("");
  const [volume, setVolume] = useState(0);

  // Initialize player and recorder
  useEffect(() => {
    playerRef.current = new AudioPlayer(proxyUrl);
    recorderRef.current = new RealtimeSpeechRecorder(proxyUrl);

    playerRef.current.setEventHandlers({
      onStateChange: setPlayback,
      onProgress: (c: number, t: number) => setProgress({ cur: c, total: t }),
      onComplete: () => setProgress({ cur: 0, total: 0 }),
      onDebugLog: (type, message, data) => {
        // Log TTS events to the log viewer
        if (type === "error") {
          logService.error("TTS", message, data);
        } else {
          logService.info(`TTS (${type})`, message, data);
        }
      },
    });

    if (sfxPlayer) {
      playerRef.current.setSfxPlayer(sfxPlayer);
    }

    recorderRef.current.setEventHandlers({
      onStateChange: setRecording,
      onPartialTranscript: setTranscript,
      onFinalTranscript: (text: string) => {
        onTranscript(text);
        setTranscript("");
      },
      onVolumeLevel: setVolume,
    });

    return () => {
      playerRef.current?.dispose();
      recorderRef.current?.cancel();
    };
  }, [proxyUrl, sfxPlayer, onTranscript]);

  // Manual play handler - loads and plays narrative
  const handlePlay = useCallback(async () => {
    if (!narrative || !enabled || !playerRef.current) return;

    try {
      // Only reload if narrative has changed since last load
      if (narrativeLoadedRef.current !== narrative) {
        await playerRef.current.load(narrative);
        narrativeLoadedRef.current = narrative;
      }
      await playerRef.current.play();
    } catch (error) {
      console.error("Failed to play TTS:", error);
    }
  }, [narrative, enabled]);

  const handlePause = useCallback(() => {
    playerRef.current?.pause();
  }, []);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
  }, []);

  // Microphone toggle handler
  const handleMic = useCallback(() => {
    const state = recorderRef.current?.getState();
    if (state === "idle" || state === "error") {
      recorderRef.current?.start();
    } else if (state === "recording" || state === "ready") {
      recorderRef.current?.stop();
    }
  }, []);

  // Microphone button styling
  const getMicButtonColor = (): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (recording) {
      case "connecting":
        return "warning";
      case "ready":
        return "success";
      case "recording":
        return "error";
      case "processing":
        return "warning";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  const getMicIcon = () => {
    if (recording === "ready" || recording === "recording") {
      return <RadioButtonChecked />;
    }
    return <Mic />;
  };

  const getRecordingLabel = () => {
    switch (recording) {
      case "connecting":
        return "Connecting...";
      case "ready":
        return "Speak now";
      case "recording":
        return "Listening";
      case "processing":
        return "Processing...";
      default:
        return "";
    }
  };

  return (
    <Box>
      {/* Live transcript display */}
      {transcript && (
        <Paper
          sx={{
            p: 2,
            mb: 1,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "error.main",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "error.main",
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Listening...
            </Typography>
          </Box>
          <Typography variant="body2">{transcript}</Typography>
        </Paper>
      )}

      {/* Control buttons */}
      <Paper sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
        {/* Enable/disable voice */}
        <IconButton
          onClick={() => {
            if (enabled) playerRef.current?.stop();
            setEnabled(!enabled);
          }}
          color={enabled ? "success" : "default"}
          title={enabled ? "Voice enabled" : "Voice disabled"}
        >
          <PowerSettingsNew />
        </IconButton>

        {enabled && (
          <>
            {/* Play/Pause */}
            <IconButton
              onClick={playback === "playing" ? handlePause : handlePlay}
              disabled={playback === "loading" || !narrative}
              color="primary"
              title={playback === "playing" ? "Pause" : "Play"}
            >
              {playback === "playing" ? <Pause /> : <PlayArrow />}
            </IconButton>

            {/* Stop */}
            <IconButton
              onClick={handleStop}
              disabled={playback === "idle"}
              color="primary"
              title="Stop"
            >
              <Stop />
            </IconButton>

            {/* Mute */}
            <IconButton
              onClick={() =>
                setMuted(playerRef.current?.toggleMute() ?? false)
              }
              color={muted ? "warning" : "default"}
            >
              {muted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>

            {/* Progress */}
            {progress.total > 0 && (
              <Typography variant="caption" color="text.secondary">
                {progress.cur + 1}/{progress.total}
              </Typography>
            )}
          </>
        )}

        {/* Divider */}
        <Box sx={{ width: 1, height: 24, bgcolor: "divider", mx: 1 }} />

        {/* Microphone */}
        <IconButton
          onClick={handleMic}
          disabled={recording === "processing" || recording === "connecting"}
          color={getMicButtonColor()}
        >
          {getMicIcon()}
        </IconButton>

        {/* Volume visualization */}
        {(recording === "recording" || recording === "ready") && (
          <Box sx={{ width: 64, height: 8, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                bgcolor: "success.main",
                width: `${volume * 100}%`,
                transition: "width 0.1s",
              }}
            />
          </Box>
        )}

        {/* Recording status */}
        {recording !== "idle" && (
          <Typography variant="caption" color="text.secondary">
            {getRecordingLabel()}
          </Typography>
        )}
      </Paper>

      {/* Loading progress bar */}
      {playback === "loading" && <LinearProgress sx={{ mt: 1 }} />}
    </Box>
  );
}
